import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import HeroImage from "../../../components/Hero/HeroImage";
import useScrollToTop from "../../../hooks/useScollToTop";
import { getQuizzVideos } from "../../../services/quizzvideoServices";
import QuizzVideoCard from "./QuizzVideoCard";
import {
  Modal,
  ModalHeader,
  ModalBody,
} from "../../../components/Modal/Modal";
import {
  FaChevronDown,
  FaExternalLinkAlt,
  FaFolderOpen,
  FaGavel,
  FaPlayCircle,
  FaVideo,
} from "react-icons/fa";

const normalize = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const sortQuizz = (items = []) => {
  return [...items].sort((a, b) => {
    const famA = normalize(a?.famille_quizz);
    const famB = normalize(b?.famille_quizz);
    if (famA !== famB) return famA.localeCompare(famB);

    const nameA = normalize(a?.nom_quizz);
    const nameB = normalize(b?.nom_quizz);
    if (nameA !== nameB) return nameA.localeCompare(nameB);

    return Number(a?.id ?? 0) - Number(b?.id ?? 0);
  });
};

const extractYoutubeId = (url = "") => {
  const value = String(url || "").trim();
  if (!value) return null;

  const patterns = [
    /youtu\.be\/([^?&/]+)/i,
    /youtube\.com\/watch\?v=([^?&/]+)/i,
    /youtube\.com\/embed\/([^?&/]+)/i,
    /youtube\.com\/shorts\/([^?&/]+)/i,
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match?.[1]) return match[1];
  }

  return null;
};

const isYoutubeUrl = (url = "") => !!extractYoutubeId(url);

const isMp4Url = (url = "") => /\.mp4(\?.*)?$/i.test(String(url || "").trim());

function StatChip({ label, value }) {
  return (
    <div className="inline-flex items-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-white backdrop-blur-sm">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-white/75">
          {label}
        </div>
        <div className="text-lg font-bold">{value}</div>
      </div>
    </div>
  );
}

function VideoPlayerContent({ quizz }) {
  const videoUrl = quizz?.lien_url_video || "";
  const youtubeId = extractYoutubeId(videoUrl);
  const youtubeEmbedUrl = youtubeId
    ? `https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`
    : null;

  if (youtubeEmbedUrl) {
    return (
      <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-black shadow-sm dark:border-slate-800">
        <div className="relative w-full overflow-hidden pt-[56.25%]">
          <iframe
            title={quizz?.nom_quizz || "Vidéo du quizz"}
            src={youtubeEmbedUrl}
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </div>
    );
  }

  if (isMp4Url(videoUrl)) {
    return (
      <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-black shadow-sm dark:border-slate-800">
        <video
          controls
          className="h-auto w-full max-h-[70vh] bg-black"
          src={videoUrl}
        >
          Votre navigateur ne supporte pas la lecture vidéo.
        </video>
      </div>
    );
  }

  return (
    <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-800">
      <p className="text-sm text-slate-600 dark:text-slate-300">
        Cette vidéo ne peut pas être prévisualisée directement ici.
      </p>

      {videoUrl ? (
        <div className="mt-4">
          <a
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            <FaExternalLinkAlt />
            Ouvrir la vidéo
          </a>
        </div>
      ) : null}
    </div>
  );
}

export default function QuizzVideoPage() {
  useScrollToTop();
  const navigate = useNavigate();

  const [quizzVideos, setQuizzVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFamily, setSelectedFamily] = useState("all");
  const [selectedQuizz, setSelectedQuizz] = useState(null);

  const refresh = async () => {
    try {
      setLoading(true);
      const data = await getQuizzVideos();
      setQuizzVideos(Array.isArray(data) ? sortQuizz(data) : []);
    } catch (error) {
      console.error("Erreur lors du chargement des quizz vidéo :", error);
      setQuizzVideos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const familyOptions = useMemo(() => {
    return [...new Set(quizzVideos.map((q) => q.famille_quizz).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b)
    );
  }, [quizzVideos]);

  const filteredQuizzVideos = useMemo(() => {
    if (selectedFamily === "all") return quizzVideos;
    return quizzVideos.filter(
      (q) => normalize(q.famille_quizz) === normalize(selectedFamily)
    );
  }, [quizzVideos, selectedFamily]);

  const stats = useMemo(() => {
    return {
      total: quizzVideos.length,
      families: familyOptions.length,
      playable: quizzVideos.filter(
        (q) => isYoutubeUrl(q.lien_url_video) || isMp4Url(q.lien_url_video)
      ).length,
    };
  }, [quizzVideos, familyOptions]);

  return (
    <div className="w-full pt-[96px] font-satoshi">
      <HeroImage
        title="Quizz vidéo d’arbitrage"
        subtitle="Visionnez les situations vidéo, analysez l’action et révélez ensuite la bonne décision arbitrale."
        imageUrl="/images/illustrations/illustration_home_004.webp"
      >
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-white/90">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="transition hover:underline"
          >
            Accueil
          </button>
          <span>/</span>
          <span>Arbitrage</span>
          <span>/</span>
          <span className="font-semibold text-white">Quizz vidéo</span>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <StatChip label="Quizz" value={stats.total} />
          <StatChip label="Familles" value={stats.families} />
          <StatChip label="Lectures directes" value={stats.playable} />
        </div>
      </HeroImage>

      <div className="mx-auto max-w-[1280px] px-4 pb-10 pt-6">
        <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-5 lg:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                Arbitrage
              </p>
              <h2 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
                Bibliothèque des vidéos quizz
              </h2>
              <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
                Sélectionnez une famille de situations arbitrales, visualisez la
                vidéo puis dévoilez la décision correcte et l’explication
                associée.
              </p>
            </div>

            <div className="w-full max-w-sm">
              <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-100">
                Filtrer par famille
              </label>

              <div className="relative">
                <select
                  value={selectedFamily}
                  onChange={(e) => setSelectedFamily(e.target.value)}
                  className="h-11 w-full appearance-none rounded-2xl border border-slate-300 bg-white px-4 pr-11 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                >
                  <option value="all">Toutes les familles</option>
                  {familyOptions.map((famille) => (
                    <option key={famille} value={famille}>
                      {famille}
                    </option>
                  ))}
                </select>

                <FaChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {filteredQuizzVideos.length} vidéo
            {filteredQuizzVideos.length > 1 ? "s" : ""}
          </span>

          {selectedFamily !== "all" && (
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
              Famille : {selectedFamily}
            </span>
          )}
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="rounded-[28px] border border-slate-200 bg-white px-6 py-14 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              Chargement des vidéos quizz…
            </div>
          ) : filteredQuizzVideos.length === 0 ? (
            <div className="rounded-[28px] border border-dashed border-slate-300 bg-white px-6 py-14 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                <FaVideo className="text-2xl" />
              </div>

              <h3 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">
                Aucun quizz vidéo disponible
              </h3>

              <p className="mx-auto mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                Aucun quizz ne correspond à la famille sélectionnée.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {filteredQuizzVideos.map((quizz) => (
                <QuizzVideoCard
                  key={quizz.id}
                  quizz={quizz}
                  onOpenVideo={() => setSelectedQuizz(quizz)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={!!selectedQuizz}
        onClose={() => setSelectedQuizz(null)}
        size="3xl"
      >
        <ModalHeader
          title={selectedQuizz?.nom_quizz || "Lecture vidéo"}
          subtitle={selectedQuizz?.famille_quizz || "Quizz arbitrage"}
          onClose={() => setSelectedQuizz(null)}
        />

        <ModalBody>
          {selectedQuizz ? (
            <div className="space-y-5">
              <VideoPlayerContent quizz={selectedQuizz} />

              <div className="rounded-3xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
                    <FaGavel />
                    {selectedQuizz.famille_quizz || "Sans famille"}
                  </span>

                  {selectedQuizz.lien_url_video ? (
                    <a
                      href={selectedQuizz.lien_url_video}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                    >
                      <FaExternalLinkAlt />
                      Ouvrir dans un nouvel onglet
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </ModalBody>
      </Modal>
    </div>
  );
}